import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, Stack, Text } from 'shell/components';

const PreviousFeedbackText = ({ feedback }) => {
    const { t } = useTranslation('assignment');
    const value = typeof feedback === 'string' ? feedback : feedback?.text;

    if (feedback?.active === false) {
        return <Text color={'light'}>{t('feedback-section.deleted-feedback', 'This feedback was deleted.')}</Text>;
    }
    if (!value) {
        return <Text color={'light'}>{t('feedback-section.no-feedback', 'No feedback provided.')}</Text>;
    }

    return (
        <Text color={'light'} rich>
            {value}
        </Text>
    );
};

const FeedbackRevertPreview = ({ feedback, feedbackType, generatedOn, onCancel, onRevert }) => {
    const { t } = useTranslation('assignment');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isRubricFeedback = feedbackType === 'rubric';

    const handleRevert = async (event) => {
        event.stopPropagation();
        setIsSubmitting(true);
        try {
            await onRevert();
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Stack
            aria-label={t('grading-section.feedback-previous-version', 'Previous Version')}
            role={'group'}
            sx={(theme) => ({
                width: '100%',
                border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
                borderRadius: '16px',
                p: 4,
                background: theme.palette.ensightful.neutral[50],
            })}
            spacing={4}
            onClick={(event) => event.stopPropagation()}
        >
            <Stack
                sx={{ rowGap: 2 }}
                alignItems={'center'}
                direction={'row'}
                flexWrap={'wrap'}
                justifyContent={'space-between'}
            >
                <Stack alignItems={'center'} direction={'row'} spacing={1}>
                    <Icon kind={'refresh'} sx={{ fontSize: '1rem' }} />
                    <Text weight={'medium'}>{t('grading-section.feedback-previous-version', 'Previous Version')}</Text>
                </Stack>
                {generatedOn && (
                    <Stack alignItems={'center'} direction={'row'} spacing={1}>
                        <Text color={'light'} variant={'p3'}>
                            {t('grading-section.feedback-generated', 'Generated')}
                        </Text>
                        <Text variant={'p3'} weight={'medium'}>
                            {t('common:ui.format-date-time-seconds-display', { val: generatedOn })}
                        </Text>
                    </Stack>
                )}
            </Stack>
            {isRubricFeedback ? (
                <Stack spacing={3}>
                    <Stack spacing={2}>
                        <Text color={'light'} variant={'p1'} weight={'medium'}>
                            {t('feedback-section.positive-title', 'What you did well')}
                        </Text>
                        <PreviousFeedbackText feedback={feedback.positive} />
                    </Stack>
                    <Stack
                        sx={(theme) => ({
                            borderRadius: '8px',
                            p: 3,
                            background: theme.palette.ensightful.neutral[100],
                        })}
                        spacing={2}
                    >
                        <Text color={'light'} variant={'p1'} weight={'medium'}>
                            {t('feedback-section.improvement-title', 'How you can improve')}
                        </Text>
                        <PreviousFeedbackText feedback={feedback.negative} />
                    </Stack>
                </Stack>
            ) : Array.isArray(feedback) ? (
                <Stack spacing={4}>
                    {feedback.map((item) => (
                        <PreviousFeedbackText key={item.id} feedback={item} />
                    ))}
                </Stack>
            ) : (
                <PreviousFeedbackText feedback={feedback} />
            )}
            <Stack direction={'row'} justifyContent={'flex-end'} spacing={3}>
                <Button
                    color={'neutral'}
                    disabled={isSubmitting}
                    size={'medium'}
                    variant={'tertiary'}
                    onClick={(event) => {
                        event.stopPropagation();
                        onCancel();
                    }}
                >
                    {t('common:controls.cancel', 'Cancel')}
                </Button>
                <Button
                    loading={isSubmitting}
                    size={'medium'}
                    startIcon={<Icon kind={'refresh'} />}
                    onClick={handleRevert}
                >
                    {t('grading-section.feedback-revert', 'Revert')}
                </Button>
            </Stack>
        </Stack>
    );
};

export default FeedbackRevertPreview;
